import moment from 'moment/moment';
import { useOrgData } from '../../../../contexts/OrgDataContext';
import { getGroupedLogs, LOGS_TYPES } from "../../../../services/log/log-service";
import { useEffect, useRef, useState } from "react";
import { Loader } from '../../../../components/loading/Loader';
import { useJournal, VISIBLE_LOGS_IN_PANEL_COUNT } from "../../../../contexts/JournalContext";
import Badge from "../../../../components/badges/Badge";
import { InboxIcon } from "@heroicons/react/20/solid";
import {
  JOURNAL_DISPLAY_ALL,
  JOURNAL_DISPLAY_PATTERNS_ONLY,
  JOURNAL_DISPLAY_ALERTS_ONLY,
  JOURNAL_DISPLAY_NON_CONFORMITY_ONLY
} from "../../../../services/journal/journal-service";


const SmoothIn = ({ children, delay = 0, onMouseEnter, onMouseLeave }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className="[&:last-child>*]:border-transparent"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "translateX(0)" : "translateX(16px)",
        transitionProperty: "opacity, transform",
        transitionDuration: "220ms",
        transitionTimingFunction: "ease-out",
        transitionDelay: `${delay}ms`,
        willChange: "opacity, transform",
      }}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}

export const JournalLogs = () => {
  const { workstationSelected, isWorkstationDataLoading } = useOrgData();
  const {
    logs,
    isLogsProcessing,
    selectedDateRange,
    allowFetchingMoreLogs,
    getMoreLogs,
    setFocusedLogDate,
    patternExecutions,
    selectedDisplayOption
  } = useJournal();

  const scrollRef = useRef(null);
  const containerLogsRef = useRef(null);

  const filteredLogs = logs.filter((log) =>
    (log.type === LOGS_TYPES.PATTERN && selectedDisplayOption.id === JOURNAL_DISPLAY_PATTERNS_ONLY) ||
    (log.type === LOGS_TYPES.ALERT && selectedDisplayOption.id === JOURNAL_DISPLAY_ALERTS_ONLY) ||
    (log.type === LOGS_TYPES.LOG1 && selectedDisplayOption.id === JOURNAL_DISPLAY_NON_CONFORMITY_ONLY) ||
    selectedDisplayOption.id === JOURNAL_DISPLAY_ALL
  );
  const { groupedLogs, dates } = getGroupedLogs(filteredLogs);

  // Handle the scroll event on the panel in two parts: managing the hovered log for positioning the timeline bar on the mini dashboard,
  // and managing the retrieval of older logs if the bottom of the log panel is reached
  useEffect(() => {
    const fetchOlderLogs = () => {
      if (!scrollRef.current || !workstationSelected || !allowFetchingMoreLogs) return;

      const totalHeight = scrollRef.current.scrollHeight;
      const windowHeight = scrollRef.current.clientHeight;
      const scrollPosition = scrollRef.current.scrollTop;

      if (scrollPosition + windowHeight >= totalHeight) {
        getMoreLogs();
      }
    };

    const handleScroll = () => {
      fetchOlderLogs();
    };

    if (scrollRef.current) {
      const scrollElement = scrollRef.current;
      scrollElement.addEventListener('scroll', handleScroll);

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollRef.current, workstationSelected, allowFetchingMoreLogs]);

  // If the machine or date filter is changed, scroll the user back to the top of the panel
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
  }, [workstationSelected, selectedDateRange]);

  const isLoading = isLogsProcessing || isWorkstationDataLoading;

  return (
    <div className='flex flex-col h-full text-perception-gray-500'>
      {isLoading && <Loader category='transparent' />}
      {
        !isLoading && filteredLogs.length === 0 &&
          <div className="flex flex-col items-center justify-center h-full text-perception-gray-700 gap-4 text-center">
            <InboxIcon className="size-24" />
            <span className="text-3xl">No logs found</span>
          </div>
      }
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden -mt-4 ">
        <div ref={containerLogsRef} className="">
          {
            dates.length > 0 && dates.map((date, dateIndex) => (
              <div key={dateIndex}>
                {/* Date of the day */}
                <div className="my-4">
                  <Badge text={moment(date).format('DD/MM/YYYY')} type="gray"/>
                </div>

                {groupedLogs[date].map((log, logIndex) => (
                  <SmoothIn
                    key={log.id ?? `${date}-${logIndex}`}
                    delay={Math.min(logIndex, VISIBLE_LOGS_IN_PANEL_COUNT) * 35}
                    onMouseEnter={() => setFocusedLogDate(log.time)}
                    onMouseLeave={() => setFocusedLogDate(null)}
                  >
                    {/* Log block*/}
                    <log.render key={logIndex} log={log} patternExecutions={patternExecutions} />
                  </SmoothIn>
                ))}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};
